<div class="app">
  <a class="skip-link" href="#roster">{{ 'SKIP_TO_CONTENT' | translate }}</a>
  <p-toast position="top-center" />

  <p class="sr-only" role="status" aria-live="polite">{{ liveMessage() }}</p>

  <!-- ==================================================== barre d'application -->
  <header class="appbar">
    <div class="appbar__inner">
      <a class="brand" href="/" [attr.aria-label]="'BRAND_HOME' | translate">
        <svg class="brand__mark" viewBox="0 0 48 48" aria-hidden="true" focusable="false">
          <defs>
            <clipPath id="efHalfL"><rect x="0" y="0" width="22" height="48" /></clipPath>
            <clipPath id="efHalfR"><rect x="26" y="0" width="22" height="48" /></clipPath>
          </defs>
          <circle cx="24" cy="24" r="23" fill="#EA580C" clip-path="url(#efHalfL)" />
          <circle cx="24" cy="24" r="23" fill="#2563EB" clip-path="url(#efHalfR)" />
          <circle cx="24" cy="24" r="8" fill="none" stroke="#FFFFFF" stroke-width="3.5" />
          <rect x="21" y="0" width="6" height="48" fill="#FFFFFF" />
          <circle cx="24" cy="24" r="3.2" fill="#047857" />
        </svg>
        <span class="brand__name">Equi<span class="brand__name-accent">Foot</span></span>
      </a>

      <div class="appbar__actions">
        @if (pwa.showInstallButton()) {
          <button
            pButton
            icon="pi pi-download"
            class="p-button-sm p-button-text install-btn"
            (click)="pwa.installApp()"
            [attr.aria-label]="'INSTALL_APP' | translate"
          >
            <span class="install-btn__label">{{ 'INSTALL_APP' | translate }}</span>
          </button>
        }

        <button
          type="button"
          class="icon-btn"
          (click)="toggleTheme()"
          [attr.aria-label]="'TOGGLE_THEME' | translate"
          [pTooltip]="'TOGGLE_THEME' | translate"
          tooltipPosition="bottom"
        >
          <i
            class="pi"
            [class.pi-moon]="theme.theme() === 'light'"
            [class.pi-sun]="theme.theme() === 'dark'"
            aria-hidden="true"
          ></i>
        </button>

        <p-select
          [options]="languages"
          [ngModel]="currentLang()"
          (ngModelChange)="changeLang($event)"
          optionLabel="label"
          optionValue="value"
          styleClass="lang-select"
          [ariaLabel]="'SELECT_LANGUAGE' | translate"
        >
          <ng-template pTemplate="selectedItem" let-selected>
            @if (selected) {
              <span class="lang-option">
                <img
                  [src]="'https://flagcdn.com/w40/' + selected.flag + '.webp'"
                  width="20"
                  height="15"
                  alt=""
                />
                <span>{{ selected.value.toUpperCase() }}</span>
              </span>
            }
          </ng-template>
          <ng-template let-lang pTemplate="item">
            <span class="lang-option">
              <img
                [src]="'https://flagcdn.com/w40/' + lang.flag + '.webp'"
                width="20"
                height="15"
                alt=""
              />
              <span>{{ lang.label }}</span>
            </span>
          </ng-template>
        </p-select>
      </div>
    </div>
  </header>

  @if (pwa.updateAvailable()) {
    <div class="update-banner" role="status">
      <i class="pi pi-sparkles" aria-hidden="true"></i>
      <span>{{ 'UPDATE_AVAILABLE' | translate }}</span>
      <button pButton class="p-button-sm" (click)="pwa.reloadApplication()">
        {{ 'RELOAD_BTN' | translate }}
      </button>
    </div>
  }

  <main id="main" class="wrap">
    <!-- ================================================================ hero -->
    <section class="hero">
      <h1>{{ 'HERO_TITLE' | translate }}</h1>
      <p class="hero__sub">{{ 'HERO_SUBTITLE' | translate }}</p>
    </section>

    <div class="layout">
      <!-- ========================================================== effectif -->
      <section id="roster" class="panel" aria-labelledby="rosterTitle">
        <div class="panel__head">
          <h2 id="rosterTitle">
            {{ 'ROSTER_TITLE' | translate }}
            <span class="ef-chip">{{ store.players().length }}</span>
          </h2>

          <div class="segmented" role="group" [attr.aria-label]="'RATING_MODE' | translate">
            <button
              type="button"
              class="segmented__option"
              [class.is-active]="ratingMode() === 'STARS'"
              [attr.aria-pressed]="ratingMode() === 'STARS'"
              (click)="setRatingMode('STARS')"
            >
              <i class="pi pi-star-fill" aria-hidden="true"></i>
              <span>{{ 'STARS_LABEL' | translate }}</span>
            </button>
            <button
              type="button"
              class="segmented__option"
              [class.is-active]="ratingMode() === 'SCORE'"
              [attr.aria-pressed]="ratingMode() === 'SCORE'"
              (click)="setRatingMode('SCORE')"
            >
              <i class="pi pi-chart-bar" aria-hidden="true"></i>
              <span>{{ 'SCORE_LABEL' | translate }}</span>
            </button>
          </div>
        </div>

        <!-- ------------------------------------------------------- composer -->
        <div class="composer">
          <div class="field field--name">
            <label class="field__label" for="playerName">{{
              'PLAYER_NAME_LABEL' | translate
            }}</label>
            <input
              #nameInput
              type="text"
              pInputText
              id="playerName"
              autocomplete="off"
              enterkeyhint="done"
              [placeholder]="'PLAYER_NAME_PLACEHOLDER' | translate"
              [ngModel]="newPlayerName()"
              (ngModelChange)="newPlayerName.set($event)"
              (keyup.enter)="addPlayer()"
            />
          </div>

          <div class="field field--level">
            <span class="field__label" id="newLevelLabel">
              {{
                (ratingMode() === 'STARS' ? 'RATING_LABEL_STARS' : 'RATING_LABEL_SCORE') | translate
              }}
            </span>

            @if (ratingMode() === 'STARS') {
              <p-rating
                [ngModel]="newPlayerStars()"
                (ngModelChange)="onNewStarsChange($event)"
                [ariaLabel]="'RATING_LABEL_STARS' | translate"
              />
            } @else {
              <div class="slider-row">
                <p-slider
                  [ngModel]="newPlayerRating()"
                  (ngModelChange)="newPlayerRating.set($event)"
                  [min]="0"
                  [max]="100"
                  styleClass="rating-slider"
                  [ariaLabel]="'RATING_LABEL_SCORE' | translate"
                />
                <output class="slider-row__value tnum">{{ newPlayerRating() }}</output>
              </div>
            }
          </div>

          <div class="composer__actions">
            <button
              pButton
              icon="pi pi-plus"
              class="composer__submit"
              (click)="addPlayer()"
              [disabled]="!newPlayerName().trim()"
            >
              {{ 'ADD_PLAYER_BTN' | translate }}
            </button>
            <button
              pButton
              icon="pi pi-list"
              class="p-button-outlined p-button-secondary"
              (click)="visibleImport.set(true)"
              [pTooltip]="'IMPORT_BTN' | translate"
              tooltipPosition="top"
              [attr.aria-label]="'IMPORT_BTN' | translate"
            ></button>
          </div>
        </div>

        <!-- -------------------------------------------------------- toolbar -->
        @if (store.players().length > 0) {
          <div class="toolbar">
            <p class="toolbar__count">
              <span class="dot" aria-hidden="true"></span>
              {{
                'PRESENT_COUNT'
                  | translate: { present: store.activeCount(), total: store.players().length }
              }}
            </p>

            <div class="toolbar__actions">
              <button type="button" class="link-btn" (click)="setAllActive(true)">
                {{ 'SELECT_ALL' | translate }}
              </button>
              <span class="sep" aria-hidden="true">·</span>
              <button type="button" class="link-btn" (click)="setAllActive(false)">
                {{ 'SELECT_NONE' | translate }}
              </button>
              <span class="sep" aria-hidden="true">·</span>
              <button type="button" class="link-btn link-btn--danger" (click)="clearRoster()">
                {{ 'CLEAR_ALL' | translate }}
              </button>
            </div>
          </div>

          @if (showSearch()) {
            <div class="search">
              <i class="pi pi-search" aria-hidden="true"></i>
              <label class="sr-only" for="rosterSearch">{{
                'SEARCH_PLACEHOLDER' | translate
              }}</label>
              <input
                type="search"
                pInputText
                id="rosterSearch"
                [placeholder]="'SEARCH_PLACEHOLDER' | translate"
                [ngModel]="search()"
                (ngModelChange)="search.set($event)"
              />
            </div>
          }
        }

        <!-- ---------------------------------------------------------- liste -->
        @if (store.players().length === 0) {
          <div class="empty">
            <svg class="empty__art" viewBox="0 0 120 80" aria-hidden="true" focusable="false">
              <rect
                x="1"
                y="1"
                width="118"
                height="78"
                rx="8"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                opacity=".35"
              />
              <line
                x1="60"
                y1="1"
                x2="60"
                y2="79"
                stroke="currentColor"
                stroke-width="1.5"
                opacity=".35"
              />
              <circle
                cx="60"
                cy="40"
                r="16"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                opacity=".35"
              />
              <circle cx="32" cy="40" r="7" fill="var(--ef-team-a)" />
              <circle cx="88" cy="40" r="7" fill="var(--ef-team-b)" />
            </svg>
            <h3>{{ 'EMPTY_ROSTER_TITLE' | translate }}</h3>
            <p>{{ 'EMPTY_ROSTER_TEXT' | translate }}</p>
            <ol class="steps">
              <li><span>1</span>{{ 'STEP_ADD' | translate }}</li>
              <li><span>2</span>{{ 'STEP_RATE' | translate }}</li>
              <li><span>3</span>{{ 'STEP_GENERATE' | translate }}</li>
            </ol>
            <button
              pButton
              icon="pi pi-list"
              class="p-button-outlined"
              (click)="visibleImport.set(true)"
            >
              {{ 'IMPORT_BTN' | translate }}
            </button>
          </div>
        } @else if (visiblePlayers().length === 0) {
          <p class="no-result">{{ 'NO_SEARCH_RESULT' | translate: { term: search() } }}</p>
        } @else {
          <ul class="roster">
            @for (player of visiblePlayers(); track player.id) {
              <li class="player" [class.is-out]="!player.active">
                <label
                  class="presence"
                  [pTooltip]="(player.active ? 'MARK_ABSENT' : 'MARK_PRESENT') | translate"
                  tooltipPosition="right"
                >
                  <input
                    type="checkbox"
                    class="sr-only"
                    [checked]="player.active"
                    (change)="toggleActive(player)"
                    [attr.aria-label]="'PRESENCE_FOR' | translate: { name: player.name }"
                  />
                  <span class="avatar" [style.--hue]="hue(player.name)" aria-hidden="true">
                    <span class="avatar__text">{{ initials(player.name) }}</span>
                    <i class="pi pi-check avatar__check" aria-hidden="true"></i>
                  </span>
                </label>

                <div class="player__id">
                  @if (editingId() === player.id) {
                    <input
                      #editInput
                      type="text"
                      pInputText
                      class="player__edit"
                      [ngModel]="editingName()"
                      (ngModelChange)="editingName.set($event)"
                      (keyup.enter)="commitEdit()"
                      (keyup.escape)="cancelEdit()"
                      (blur)="commitEdit()"
                      [attr.aria-label]="'RENAME_PLAYER' | translate"
                    />
                  } @else {
                    <button
                      type="button"
                      class="player__name"
                      (click)="startEdit(player)"
                      [attr.aria-label]="'RENAME_PLAYER_NAMED' | translate: { name: player.name }"
                    >
                      <span class="player__name-text">{{ player.name }}</span>
                      <i class="pi pi-pencil" aria-hidden="true"></i>
                    </button>
                    @if (!player.active) {
                      <span class="tag tag--out">{{ 'ABSENT' | translate }}</span>
                    } @else if (player.benched) {
                      <span class="tag tag--bench">{{ 'BENCHED' | translate }}</span>
                    }
                  }
                </div>

                <div class="player__level">
                  @if (ratingMode() === 'STARS') {
                    <p-rating
                      [ngModel]="displayRating(player)"
                      (ngModelChange)="onPlayerStarsChange(player, $event)"
                      [ariaLabel]="'RATING_FOR' | translate: { name: player.name }"
                    />
                  } @else {
                    <span class="level-meter" aria-hidden="true">
                      <span class="level-meter__fill" [style.width.%]="player.rating"></span>
                    </span>
                    <input
                      type="number"
                      pInputText
                      class="level-input tnum"
                      inputmode="numeric"
                      min="0"
                      max="100"
                      [value]="player.rating"
                      (change)="onPlayerScoreChange(player, $any($event.target))"
                      [attr.aria-label]="'RATING_FOR' | translate: { name: player.name }"
                    />
                  }
                </div>

                @if (player.active) {
                  <button
                    type="button"
                    class="icon-btn player__bench"
                    [class.is-benched]="player.benched"
                    (click)="toggleBenched(player)"
                    [pTooltip]="(player.benched ? 'UNMARK_BENCHED' : 'MARK_BENCHED') | translate"
                    tooltipPosition="left"
                    [attr.aria-pressed]="player.benched"
                    [attr.aria-label]="
                      (player.benched ? 'UNMARK_BENCHED_NAMED' : 'MARK_BENCHED_NAMED')
                        | translate: { name: player.name }
                    "
                  >
                    <i class="pi pi-inbox" aria-hidden="true"></i>
                  </button>
                }

                <button
                  type="button"
                  class="icon-btn icon-btn--danger player__remove"
                  (click)="removePlayer(player)"
                  [attr.aria-label]="'REMOVE_PLAYER_NAMED' | translate: { name: player.name }"
                >
                  <i class="pi pi-trash" aria-hidden="true"></i>
                </button>
              </li>
            }
          </ul>
        }
      </section>

      <!-- =========================================================== équipes -->
      <aside class="side" aria-labelledby="resultsTitle">
        <div class="panel results">
          <div class="panel__head">
            <h2 id="resultsTitle">{{ 'RESULTS_TITLE' | translate }}</h2>
            <div class="results__tools">
              <p-select
                [options]="teamSizeOptions"
                [ngModel]="store.teamSize()"
                (ngModelChange)="onTeamSizeChange($event)"
                optionValue="value"
                styleClass="size-select"
                [placeholder]="'TEAM_SIZE_AUTO' | translate"
                [ariaLabel]="'TEAM_SIZE_LABEL' | translate"
              >
                <ng-template pTemplate="selectedItem" let-selected>
                  @if (selected?.value) {
                    <span>{{ selected.value }} vs {{ selected.value }}</span>
                  } @else {
                    <span>{{ 'TEAM_SIZE_AUTO' | translate }}</span>
                  }
                </ng-template>
                <ng-template pTemplate="item" let-option>
                  @if (option.value) {
                    <span>{{ option.value }} vs {{ option.value }}</span>
                  } @else {
                    <span>{{ 'TEAM_SIZE_AUTO' | translate }}</span>
                  }
                </ng-template>
              </p-select>
              @if (store.canGenerate()) {
                <span class="ef-chip">{{ teamSizeHint().a }} vs {{ teamSizeHint().b }}</span>
              }
            </div>
          </div>

          @if (store.composition(); as comp) {
            @if (store.compositionStale()) {
              <p class="notice" role="status">
                <i class="pi pi-exclamation-circle" aria-hidden="true"></i>
                {{ 'STALE_NOTICE' | translate }}
              </p>
            }

            @if (store.needsBench()) {
              <p class="notice" role="status">
                <i class="pi pi-inbox" aria-hidden="true"></i>
                {{ 'NEED_BENCH_COUNT' | translate: { count: store.surplus() } }}
              </p>
            }

            <div class="balance">
              <div class="balance__track">
                <span class="balance__a" [style.width.%]="comp.metrics.balance"></span>
                <span class="balance__b"></span>
              </div>
              <p class="balance__legend">
                <span class="tnum">{{ toScale(comp.metrics.teamAScore) }}</span>
                <strong class="balance__gap">
                  {{ 'GAP_LABEL' | translate }}
                  <span class="tnum">{{ toScale(comp.metrics.diff) }}</span>
                </strong>
                <span class="tnum">{{ toScale(comp.metrics.teamBScore) }}</span>
              </p>
            </div>

            <div class="teams">
              <article class="team team--a">
                <header class="team__head">
                  <span class="bib" aria-hidden="true">A</span>
                  <h3>{{ 'TEAM_A' | translate }}</h3>
                  <span class="team__count tnum">{{ comp.teamA.length }}</span>
                </header>
                <ul class="team__list">
                  @for (player of comp.teamA; track player.id) {
                    <li>
                      <span class="team__player">{{ player.name }}</span>
                      <span class="team__rating tnum">
                        {{ ratingMode() === 'STARS' ? displayRating(player) + '★' : player.rating }}
                      </span>
                    </li>
                  }
                </ul>
                <footer class="team__foot">
                  {{ 'AVERAGE_LABEL' | translate }}
                  <strong class="tnum">{{ toScale(comp.metrics.teamAAvg) }}</strong>
                </footer>
              </article>

              <article class="team team--b">
                <header class="team__head">
                  <span class="bib" aria-hidden="true">B</span>
                  <h3>{{ 'TEAM_B' | translate }}</h3>
                  <span class="team__count tnum">{{ comp.teamB.length }}</span>
                </header>
                <ul class="team__list">
                  @for (player of comp.teamB; track player.id) {
                    <li>
                      <span class="team__player">{{ player.name }}</span>
                      <span class="team__rating tnum">
                        {{ ratingMode() === 'STARS' ? displayRating(player) + '★' : player.rating }}
                      </span>
                    </li>
                  }
                </ul>
                <footer class="team__foot">
                  {{ 'AVERAGE_LABEL' | translate }}
                  <strong class="tnum">{{ toScale(comp.metrics.teamBAvg) }}</strong>
                </footer>
              </article>
            </div>

            @if (comp.bench.length > 0) {
              <article class="bench">
                <header class="bench__head">
                  <i class="pi pi-inbox" aria-hidden="true"></i>
                  <h3>{{ 'BENCH_TITLE' | translate }}</h3>
                  <span class="team__count tnum">{{ comp.bench.length }}</span>
                </header>
                <ul class="bench__list">
                  @for (player of comp.bench; track player.id) {
                    <li>
                      <span class="team__player">{{ player.name }}</span>
                      <span class="team__rating tnum">
                        {{ ratingMode() === 'STARS' ? displayRating(player) + '★' : player.rating }}
                      </span>
                    </li>
                  }
                </ul>
              </article>
            }

            <div class="results__actions">
              <button
                pButton
                icon="pi pi-refresh"
                class="results__primary"
                (click)="generate()"
                [disabled]="!store.canGenerate()"
              >
                {{ 'REGENERATE_BTN' | translate }}
              </button>

              <div class="results__share">
                <button
                  pButton
                  icon="pi pi-copy"
                  class="p-button-outlined p-button-secondary p-button-sm"
                  (click)="copyRecap()"
                >
                  {{ 'COPY_TEXT_BTN' | translate }}
                </button>
                <button
                  pButton
                  icon="pi pi-whatsapp"
                  class="p-button-outlined p-button-secondary p-button-sm"
                  (click)="shareOnWhatsApp()"
                >
                  {{ 'SHARE_WHATSAPP_BTN' | translate }}
                </button>
                <button
                  pButton
                  icon="pi pi-link"
                  class="p-button-outlined p-button-secondary p-button-sm"
                  (click)="copyLink()"
                >
                  {{ 'COPY_LINK_BTN' | translate }}
                </button>
              </div>
            </div>
          } @else {
            <div class="results__empty">
              <p>{{ 'RESULTS_EMPTY_TEXT' | translate }}</p>
              <button
                pButton
                icon="pi pi-bolt"
                class="results__primary"
                (click)="generate()"
                [disabled]="!store.canGenerate()"
              >
                {{ 'GENERATE_TEAMS_BTN' | translate }}
              </button>
              @if (store.needsBench()) {
                <p class="results__hint">
                  {{ 'NEED_BENCH_COUNT' | translate: { count: store.surplus() } }}
                </p>
              } @else if (!store.canGenerate()) {
                <p class="results__hint">{{ 'NEED_TWO_PLAYERS' | translate }}</p>
              }
            </div>
          }
        </div>
      </aside>
    </div>
  </main>

  <footer class="footer">
    <div class="footer__inner">
      <p class="footer__copy">{{ 'COPYRIGHT' | translate }}</p>
      <div class="footer__links">
        <button type="button" class="link" (click)="visibleContact.set(true)">
          {{ 'CONTACT_BTN' | translate }}
        </button>
        <button type="button" class="link" (click)="visibleLegal.set(true)">
          {{ 'LEGAL_LINK' | translate }}
        </button>
        <a class="link" href="https://github.com/talipcr" target="_blank" rel="noopener noreferrer">
          <i class="pi pi-github" aria-hidden="true"></i>
          <span>{{ 'GITHUB_LINK' | translate }}</span>
        </a>
      </div>
    </div>
  </footer>

  <!-- ============================================ barre d'action (mobile) -->
  @if (store.players().length > 0) {
    <div class="actionbar">
      <div class="actionbar__info">
        <strong class="tnum">{{ store.activeCount() }}</strong>
        <span>{{ 'PRESENT_SHORT' | translate }}</span>
      </div>
      <button
        pButton
        [icon]="store.composition() ? 'pi pi-refresh' : 'pi pi-bolt'"
        class="actionbar__cta"
        (click)="generate()"
        [disabled]="!store.canGenerate()"
      >
        {{ (store.composition() ? 'REGENERATE_BTN' : 'GENERATE_TEAMS_BTN') | translate }}
      </button>
    </div>
  }

  <!-- ==================================================== annuler suppression -->
  @if (removed(); as item) {
    <div class="snackbar" role="status">
      <span class="snackbar__text">{{
        'PLAYER_REMOVED' | translate: { name: item.player.name }
      }}</span>
      <button type="button" class="snackbar__action" (click)="undoRemove()">
        {{ 'UNDO' | translate }}
      </button>
      <button
        type="button"
        class="snackbar__close"
        (click)="dismissUndo()"
        [attr.aria-label]="'CLOSE' | translate"
      >
        <i class="pi pi-times" aria-hidden="true"></i>
      </button>
    </div>
  }

  @defer (on idle) {
    <app-import-dialog [(visible)]="visibleImport" (playersImported)="onPlayersImported($event)" />
    <app-legal-dialog [(visible)]="visibleLegal" />
    <app-contact-dialog [(visible)]="visibleContact" />
  }
</div>
